/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-computed-properties-in-native-classes, ember/no-get, ember/no-runloop, no-restricted-properties, warp-drive/no-legacy-request-patterns */
import Component from '@ember/component';
import { tracked } from '@glimmer/tracking';
import { set, action, computed } from '@ember/object';
import { later } from '@ember/runloop';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import InputType from '@linc-technologies/linced-common/enums/input-type';
import Scopes from '@linc-technologies/linced-common/enums/scopes';
import { defaultTinyMCEConfig } from '@linc-technologies/linced-common/utils/tinymce';
import { tagName } from '@ember-decorators/component';

@tagName('')
export default class FormSection extends Component {
  @service
  store;

  // Attrs
  InputType = InputType;

  Scopes = Scopes;

  @tracked
  editingField = null;

  editorOptions = Object.freeze(defaultTinyMCEConfig());

  // State
  @tracked
  isNewField = false;

  @tracked
  fieldDialogOpen = false;

  @tracked
  fieldIsRequired = false;

  @action
  setFieldIsRequired(value) {
    this.fieldIsRequired = value;
  }

  @tracked
  fieldLabel = '';

  @action
  setFieldLabel(value) {
    this.fieldLabel = value;
  }

  @tracked
  fieldStaticContent = '';

  @action
  setFieldStaticContent(value) {
    this.fieldStaticContent = value;
  }

  @tracked
  savingField = false;

  isAdminSite = false;

  @tracked
  fieldSearchText;

  @action
  setFieldSearchText(value) {
    this.fieldSearchText = value;
  }

  @tracked
  selectedField;

  @action
  setSelectedField(value) {
    this.selectedField = value;
  }

  // Props
  addField() {}

  // Methods
  @computed('editingField.inputType')
  get editingFieldType() {
    return InputType.getLabel(this.editingField.inputType);
  }

  @computed('editingField.inputType')
  get isStaticContent() {
    return (
      this.editingField.inputType === InputType.TITLE ||
      this.editingField.inputType === InputType.DESCRIPTION
    );
  }

  @computed('editingField.fieldId')
  get originalFieldLabel() {
    let fieldId = this.get('editingField.fieldId');
    if (isEmpty(fieldId)) {
      return '';
    }
    let field = this.store.peekRecord('people-schema/field', fieldId);
    if (isEmpty(field)) {
      return '';
    }
    return field.label;
  }

  // _newField returns a new model schema field.
  _newField(inputType, label, content, index) {
    return {
      fieldId: '',
      index: index,
      isSiblingLinked: false,
      key: '',
      inputType: inputType,
      dataType: '',
      label: label,
      default: null,
      placeholder: '',
      options: null,
      optionsFromEnum: false,
      htmlAttributes: null,
      isRequired: false,
      staticContent: content,
      isObsolete: false,
    };
  }

  @action
  handleAddField(section, field) {
    this.addField(section, field);
    this.selectedField = null;
    this.fieldSearchText = '';
  }

  // addStaticContent generates and returns a new static content field for
  // enrolment form presentation/layout purposes.
  @action
  addStaticContent(section, inputType) {
    let i = section.fields.length || 0;
    let newStaticContent = this._newField(
      inputType,
      `New ${InputType.getLabel(inputType)}`,
      '',
      i,
    );

    this.editingField = newStaticContent;
    this.isNewField = true;
    this.fieldStaticContent = '';
    this.fieldDialogOpen = true;
  }

  @action
  editField(section, field) {
    this.editingField = field;
    this.fieldLabel = field.get('label');
    this.fieldIsRequired = field.get('isRequired');
    this.fieldDialogOpen = true;
    this.fieldStaticContent = field.get('staticContent');
  }

  @action
  saveField(section) {
    let field = this.editingField;
    this.savingField = true;
    if (this.isStaticContent) {
      // we use set here as the field is a pojo when new, therefore doesn't
      // extend from ember.object.
      set(field, 'staticContent', this.fieldStaticContent);

      if (this.isNewField) {
        section.fields.pushObject(field);
      }
    } else {
      set(field, 'label', this.fieldLabel);
      set(field, 'isRequired', this.fieldIsRequired);
    }

    later(
      this,
      () => {
        // We don't know why TinyMCE blows out on this dialog
        // in particular, but having this here seems to fix it on
        // destroy.
        this.fieldDialogOpen = false;
        this.isNewField = false;
        this.savingField = false;
      },
      250,
    );
  }

  @action
  closeFieldDialog() {
    this.fieldDialogOpen = false;
    this.isNewField = false;
  }

  @action
  showActions(field) {
    return (
      !(field.key === 'core:subtype' || field.key === 'subtype') ||
      this.isAdminSite
    );
  }
}
